/**
 * @file analytics.service.ts
 * @description Dedicated service for venue traffic metrics, order metrics, feedback, and live pulse telemetry.
 * 
 * CORE RESPONSIBILITIES:
 * 1. Aggregating multi-day page view logs and interactive menu clicks.
 * 2. Revenue and ticket breakdown calculations.
 * 3. Guest ratings and sentiment telemetry.
 * 4. Multi-step conversion funnels, item conversion matrix, and attribution.
 * 5. Real-time pulse feed for live floor operations.
 */

import { supabase } from '../../../lib/supabase';
import type { InsightsMetrics, PulseLiveActivity } from '../types';
import {
  computeFunnelMetrics,
  computeItemConversions,
  computeCategoryPopularity,
  computeSearchDemand,
  computeTrafficAttribution,
  computePeakPeriods,
  generateAutomatedInsights,
} from './analyticsAggregations';

/**
 * Fetches comprehensive operational and marketing insights for a venue.
 * Uses optimized server-side RPC `get_venue_insights` with client-side aggregation fallback.
 */
export const fetchVenueInsights = async (venueId: string, days = 30): Promise<InsightsMetrics> => {
  const daysAgo = new Date();
  daysAgo.setDate(daysAgo.getDate() - days);
  const daysAgoStr = daysAgo.toISOString();
  const nowStr = new Date().toISOString();

  // 1. Primary backend-driven calculation RPC (Rule 15.1: Backend Calculates Everything)
  try {
    const { data: summary, error } = await supabase.rpc('get_venue_analytics_summary', {
      p_venue_id: venueId,
      p_start_date: daysAgoStr,
      p_end_date: nowStr,
    });

    if (!error && summary) {
      const itemConversions = summary.itemConversions || [];
      const topInteracted = itemConversions.slice(0, 5).map((i: any) => ({ name: i.name, count: i.views }));
      const topOrdered = itemConversions.filter((i: any) => i.orders > 0).slice(0, 5).map((i: any) => ({ name: i.name, count: i.orders }));

      return {
        totalOrders: Number(summary.totalOrders || 0),
        totalRevenue: Number(summary.totalRevenue || 0),
        avgOrderValue: Number(summary.avgOrderValue || 0),
        completedOrders: Number(summary.completedOrders || 0),
        pendingOrders: Number(summary.pendingOrders || 0),
        cancelledOrders: Number(summary.cancelledOrders || 0),
        ordersByDay: summary.ordersByDay || [],
        totalPageViews: Number(summary.totalPageViews || 0),
        totalInteractions: Number(summary.totalInteractions || 0),
        viewsByDay: summary.viewsByDay || [],
        topRatedItems: [],
        topInteractedItems: topInteracted,
        topOrderedItems: topOrdered,
        recentFeedback: [],
        avgRating: Number(summary.avgRating || 0),
        ratingCount: Number(summary.ratingCount || 0),
        totalWaiterRequests: Number(summary.totalWaiterRequests || 0),
        pendingRequests: Number(summary.pendingRequests || 0),
        requestTypes: [],
        funnel: summary.funnel,
        itemConversions: itemConversions,
        topSearches: summary.topSearches || [],
        unmetSearches: summary.unmetSearches || [],
        trafficSources: summary.trafficSources || [],
        sourceAttributions: summary.trafficSources || [],
        deviceBreakdown: summary.deviceBreakdown || [],
        categoryPopularity: summary.categoryPopularity || [],
        peakPeriods: summary.peakPeriods,
        automatedInsights: generateAutomatedInsights(
          summary.funnel,
          { topRevenue: topOrdered, topConversion: topInteracted },
          summary.unmetSearches || [],
          summary.trafficSources || [],
          summary.peakPeriods
        ),
      };
    }
  } catch (rpcErr) {
    console.warn('[analytics.service] get_venue_analytics_summary RPC error, falling back:', rpcErr);
  }

  // Fallback if RPC is unavailable
  let baseInsights: any = null;

  // Query operational tables for 10-event architecture and granular item breakdowns
  const [
    ordersRes,
    orderItemsRes,
    viewsRes,
    rawEventsRes,
    ratingsRes,
    waiterRes,
    topRatedRes,
    itemsRes,
    categoriesRes,
  ] = await Promise.all([
    supabase
      .from('orders')
      .select('id, total_amount, status, payment_status, created_at, type, metadata')
      .eq('venue_id', venueId)
      .gte('created_at', daysAgoStr),
    supabase
      .from('order_items')
      .select('id, order_id, item_id, price, quantity, created_at')
      .gte('created_at', daysAgoStr),
    baseInsights
      ? Promise.resolve({ data: [] })
      : supabase
          .from('analytics')
          .select('id, created_at')
          .eq('venue_id', venueId)
          .eq('event_type', 'page_view')
          .gte('created_at', daysAgoStr),
    supabase
      .from('analytics')
      .select('id, event_type, metadata, created_at')
      .eq('venue_id', venueId)
      .gte('created_at', daysAgoStr)
      .limit(10000),
    baseInsights
      ? Promise.resolve({ data: [] })
      : supabase
          .from('item_feedback')
          .select('rating, comment, created_at')
          .eq('venue_id', venueId)
          .order('created_at', { ascending: false })
          .limit(10),
    supabase
      .from('waiter_requests')
      .select('id, type, status, created_at')
      .eq('venue_id', venueId)
      .gte('created_at', daysAgoStr),
    baseInsights
      ? Promise.resolve({ data: [] })
      : supabase
          .from('items')
          .select('name, avg_rating, rating_count')
          .eq('venue_id', venueId)
          .gt('rating_count', 0)
          .order('avg_rating', { ascending: false })
          .limit(5),
    supabase
      .from('items')
      .select('id, name, price, category_id')
      .eq('venue_id', venueId),
    supabase
      .from('categories')
      .select('id, name')
      .eq('venue_id', venueId),
  ]);

  const orders = ordersRes.data || [];
  const allOrderItems = orderItemsRes.data || [];
  const views = viewsRes.data || [];
  const rawEvents = rawEventsRes.data || [];
  const feedback = ratingsRes.data || [];
  const waiterRequests = waiterRes.data || [];
  const items = itemsRes.data || [];
  const categories = categoriesRes.data || [];

  // Filter order items to only orders belonging to this venue
  const venueOrderIds = new Set(orders.map((o) => o.id));
  const venueOrderItems = allOrderItems.filter((oi) => venueOrderIds.has(oi.order_id));

  const topRatedItems = (topRatedRes.data || []).map((item: any) => ({
    name: String(item.name || ''),
    avg_rating: Number(item.avg_rating || 0),
    rating_count: Number(item.rating_count || 0),
  }));

  // Calculate order metrics if baseInsights is not available
  const isCompleted = (status: unknown) => String(status || '').toUpperCase() === 'COMPLETED';
  const completedOrders = orders.filter((o) => isCompleted(o.status));
  const totalRevenue = completedOrders.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0);
  const avgOrderValue = completedOrders.length ? Math.round(totalRevenue / completedOrders.length) : 0;

  // Build daily timeline
  const daysMap: Record<string, { orders: number; revenue: number; views: number }> = {};
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const key = d.toISOString().slice(5, 10);
    daysMap[key] = { orders: 0, revenue: 0, views: 0 };
  }

  orders.forEach((o) => {
    const key = o.created_at.slice(5, 10);
    if (daysMap[key]) {
      daysMap[key].orders += 1;
      if (isCompleted(o.status)) {
        daysMap[key].revenue += Number(o.total_amount) || 0;
      }
    }
  });

  views.forEach((v) => {
    const key = v.created_at.slice(5, 10);
    if (daysMap[key]) {
      daysMap[key].views += 1;
    }
  });

  const ordersByDay = Object.entries(daysMap).map(([day, val]) => ({
    day,
    orders: val.orders,
    revenue: val.revenue,
  }));

  const viewsByDay = Object.entries(daysMap).map(([day, val]) => ({
    day,
    views: val.views,
  }));

  // Waiter request types
  const typeMap: Record<string, number> = {};
  waiterRequests.forEach((r) => {
    typeMap[r.type] = (typeMap[r.type] || 0) + 1;
  });
  const requestTypes = Object.entries(typeMap).map(([type, count]) => ({ type, count }));

  // Collect distinct session IDs
  const sessionsSet = new Set<string>();

  rawEvents.forEach((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    if (meta.session_id) sessionsSet.add(meta.session_id);
  });

  const totalPageViews = Number(baseInsights?.totalPageViews ?? views.length);

  // Modular calculation delegators
  const funnel = computeFunnelMetrics(sessionsSet, rawEvents, orders, totalPageViews);
  const itemConversionsData = computeItemConversions(items, venueOrderItems, rawEvents);
  const categoryPopularity = computeCategoryPopularity(categories, items, venueOrderItems, rawEvents);
  const { topSearches, unmetSearches } = computeSearchDemand(rawEvents);
  const { trafficSources, sourceAttributions, deviceBreakdown } = computeTrafficAttribution(rawEvents, orders);
  const peakPeriods = computePeakPeriods(rawEvents, orders);
  const automatedInsights = generateAutomatedInsights(
    funnel,
    itemConversionsData,
    unmetSearches,
    sourceAttributions,
    peakPeriods
  );

  return {
    totalOrders: Number(baseInsights?.totalOrders ?? orders.length),
    totalRevenue: Number(baseInsights?.totalRevenue ?? totalRevenue),
    avgOrderValue: Number(baseInsights?.avgOrderValue ?? avgOrderValue),
    completedOrders: Number(baseInsights?.completedOrders ?? completedOrders.length),
    pendingOrders: Number(baseInsights?.pendingOrders ?? orders.filter((o) => String(o.status || '').toUpperCase() === 'PENDING').length),
    cancelledOrders: Number(baseInsights?.cancelledOrders ?? orders.filter((o) => String(o.status || '').toUpperCase() === 'CANCELLED').length),
    ordersByDay: baseInsights?.ordersByDay ?? ordersByDay,
    totalPageViews,
    totalInteractions: funnel.item_views > 0 ? funnel.item_views : Number(baseInsights?.totalInteractions ?? 0),
    viewsByDay: baseInsights?.viewsByDay ?? viewsByDay,
    topRatedItems: baseInsights?.topRatedItems ?? topRatedItems,
    topInteractedItems: itemConversionsData.topInteracted.length > 0 ? itemConversionsData.topInteracted : (baseInsights?.topInteractedItems ?? []),
    topOrderedItems: itemConversionsData.topOrdered,
    recentFeedback: baseInsights?.recentFeedback ?? feedback,
    avgRating: Number(baseInsights?.avgRating ?? (feedback.length ? feedback.reduce((sum, f) => sum + f.rating, 0) / feedback.length : 0)),
    ratingCount: Number(baseInsights?.ratingCount ?? feedback.length),
    totalWaiterRequests: Number(baseInsights?.totalWaiterRequests ?? waiterRequests.length),
    pendingRequests: Number(baseInsights?.pendingRequests ?? waiterRequests.filter((r) => String(r.status || '').toLowerCase() === 'pending').length),
    requestTypes: baseInsights?.requestTypes ?? requestTypes,
    // Expanded Architecture Additions
    funnel,
    itemConversions: itemConversionsData.conversions,
    topSearches,
    unmetSearches,
    trafficSources,
    sourceAttributions,
    deviceBreakdown,
    categoryPopularity,
    peakPeriods,
    automatedInsights,
  };
};

/**
 * Fetches near-real-time operational activity for the Pulse Console.
 */
export const fetchPulseLiveData = async (venueId: string): Promise<PulseLiveActivity> => {
  // 1. Primary backend-driven calculation RPC (Rule 15.1: Backend Calculates Everything)
  try {
    const { data: pulse, error } = await supabase.rpc('get_venue_pulse_summary', {
      p_venue_id: venueId,
    });

    if (!error && pulse) {
      return {
        activeGuestsNow: Number(pulse.activeGuestsNow || 0),
        activeSessionsToday: Number(pulse.activeSessionsToday || 0),
        ordersToday: Number(pulse.ordersToday || 0),
        revenueToday: Number(pulse.revenueToday || 0),
        avgOrderValueToday: Number(pulse.avgOrderValueToday || 0),
        openOrders: Number(pulse.openOrders || 0),
        pendingWaiterRequests: Number(pulse.pendingWaiterRequests || 0),
        currentlyViewing: pulse.currentlyViewing || [],
        recentEvents: pulse.recentEvents || [],
        alerts: pulse.alerts || [],
        orderRail: pulse.orderRail || { new: 0, preparing: 0, ready: 0 },
      };
    }
  } catch (rpcErr) {
    console.warn('[analytics.service] get_venue_pulse_summary RPC error, falling back:', rpcErr);
  }

  // Fallback if RPC is unavailable
  const now = new Date();
  const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000).toISOString();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();

  const [recentEventsRes, todayOrdersRes, pendingWaitersRes] = await Promise.all([
    supabase
      .from('analytics')
      .select('id, event_type, metadata, created_at')
      .eq('venue_id', venueId)
      .gte('created_at', fifteenMinutesAgo)
      .order('created_at', { ascending: false })
      .limit(200),
    supabase
      .from('orders')
      .select('id, total_amount, status, created_at')
      .eq('venue_id', venueId)
      .gte('created_at', startOfToday),
    supabase
      .from('waiter_requests')
      .select('id, type, status, created_at')
      .eq('venue_id', venueId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true }),
  ]);

  const recentEvents = recentEventsRes.data || [];
  const todayOrders = todayOrdersRes.data || [];
  const pendingWaiters = pendingWaitersRes.data || [];

  const activeGuestsSet = new Set<string>();
  const itemGuestsMap = new Map<string, Set<string>>();
  const itemViewsMap = new Map<string, number>();

  recentEvents.forEach((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    const sessionId = meta.session_id || meta.table_id || 'guest_session';
    if (meta.session_id) activeGuestsSet.add(meta.session_id);
    if (meta.item_name) {
      if (!itemGuestsMap.has(meta.item_name)) {
        itemGuestsMap.set(meta.item_name, new Set());
      }
      itemGuestsMap.get(meta.item_name)!.add(sessionId);
      itemViewsMap.set(meta.item_name, (itemViewsMap.get(meta.item_name) || 0) + 1);
    }
  });

  const currentlyViewing = Array.from(itemGuestsMap.entries())
    .map(([name, guestsSet]) => ({
      name,
      count: guestsSet.size,
      views: itemViewsMap.get(name) || guestsSet.size,
    }))
    .sort((a, b) => b.count - a.count || (b.views || 0) - (a.views || 0))
    .slice(0, 5);

  const completedToday = todayOrders.filter((o) => String(o.status || '').toUpperCase() === 'COMPLETED');
  const revenueToday = completedToday.reduce((sum, o) => sum + (Number(o.total_amount) || 0), 0);
  const avgOrderValueToday = completedToday.length > 0 ? Math.round(revenueToday / completedToday.length) : 0;
  const openOrders = todayOrders.filter((o) => ['PENDING', 'PREPARING', 'READY'].includes(String(o.status || '').toUpperCase())).length;

  const alerts: string[] = [];
  if (openOrders >= 5) {
    alerts.push(`High order volume: ${openOrders} active tickets currently on the kitchen rail.`);
  }
  if (pendingWaiters.length >= 3) {
    alerts.push(`Staff call surge: ${pendingWaiters.length} tables currently requesting assistance.`);
  }
  if (activeGuestsSet.size >= 10) {
    alerts.push(`High footfall: ${activeGuestsSet.size} active guests browsing the menu right now.`);
  }

  const formattedEvents = recentEvents.slice(0, 10).map((evt) => {
    const meta = (evt.metadata && typeof evt.metadata === 'object' ? evt.metadata : {}) as Record<string, any>;
    const eventName = meta.event_name || evt.event_type;
    let title = 'Guest interaction';
    if (eventName === 'item_view') title = `Viewed ${meta.item_name || 'Dish'}`;
    else if (eventName === 'order_item_added') title = `Added ${meta.item_name || 'Dish'} to cart`;
    else if (eventName === 'order_submitted') title = 'Submitted new order';
    else if (eventName === 'waiter_request_created') title = `Table ${meta.table_id || ''} called staff`;
    else if (eventName === 'rating_submitted') title = `Rated ${meta.item_name || 'Dish'} (${meta.rating}★)`;

    return {
      id: evt.id,
      event_name: eventName,
      title,
      timestamp: evt.created_at,
    };
  });

  const newOrdersCount = todayOrders.filter((o) => ['PENDING', 'NEW'].includes(String(o.status || '').toUpperCase())).length;
  const preparingOrdersCount = todayOrders.filter((o) => String(o.status || '').toUpperCase() === 'PREPARING').length;
  const readyOrdersCount = todayOrders.filter((o) => String(o.status || '').toUpperCase() === 'READY').length;
  const completedOrdersCount = completedToday.length;

  const nowMs = Date.now();
  const waiterQueue = pendingWaiters.map((w) => ({
    id: w.id,
    type: w.type || 'Staff Assistance',
    elapsedMinutes: Math.max(1, Math.round((nowMs - new Date(w.created_at).getTime()) / 60000)),
  }));

  return {
    activeGuestsNow: activeGuestsSet.size > 0 ? activeGuestsSet.size : Math.min(recentEvents.length, 3),
    activeSessionsToday: Math.max(activeGuestsSet.size, todayOrders.length),
    ordersToday: todayOrders.length,
    revenueToday,
    avgOrderValueToday,
    openOrders,
    pendingWaiterRequests: pendingWaiters.length,
    currentlyViewing,
    recentEvents: formattedEvents,
    alerts,
    orderRail: {
      new: newOrdersCount,
      preparing: preparingOrdersCount,
      ready: readyOrdersCount,
      completed: completedOrdersCount,
    },
    waiterQueue,
  };
};
